import { z } from 'zod';
import { PERMISSION_KEYS } from './permissions.js';
import { AUDIT_EVENT_TYPE_KEYS } from './audit-events.js';
import { isValidTimeZone } from './time.js';
import {
  ENVIRONMENTS,
  EXECUTION_MODES,
  EXPLORER_OBJECT_TYPES,
  HTTP_METHODS,
  isServerScopedKind,
  JOB_STATUSES,
  OUTPUT_FORMATS,
  SHAREABLE_VISIBILITIES,
  SQL_DIALECTS,
  STATEMENT_CLASSES,
  TARGET_KINDS,
} from './types.js';

/**
 * Zod schemas for every request boundary. The server validates with these and
 * the SPA reuses them for client-side form validation, so the two can never
 * disagree about what a valid submission looks like.
 */

// --------------------------------------------------------------- primitives

export const uuidSchema = z.string().uuid();
export const isoDateSchema = z.string().datetime({ offset: true });

/** Target keys appear in audit records and policies, so keep them boring. */
export const targetKeySchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Use lowercase letters, digits and hyphens');

export const justificationSchema = z
  .string()
  .trim()
  .min(10, 'Explain why this is being run - auditors read this field')
  .max(2000);

/**
 * A SQL Server database or schema identifier, as a value rather than as text
 * that becomes syntax.
 *
 * Every place a database name is accepted it ends up in one of two positions:
 * a bound parameter in a catalogue query, or a bracket-quoted identifier in a
 * generated statement. The first is safe by construction; the second is not,
 * because `]` closes the quoting. SQL Server itself permits almost anything
 * inside brackets, so this is narrower than the server would allow -- and
 * narrow on purpose. A database called `Banner]; DROP` is not a database
 * anybody has, and refusing it means the bracket-quoting in the explorer is
 * sound without a second escaping layer to get wrong.
 */
export const databaseNameSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(
    /^[A-Za-z_][A-Za-z0-9_$#@ -]*$/,
    'Use letters, digits, spaces, and _ $ # @ or - , starting with a letter or underscore',
  );

/**
 * Statement size cap. Large enough for a genuinely complex report query,
 * small enough that a pasted data dump is rejected before it reaches a driver.
 */
export const sqlStatementSchema = z
  .string()
  .trim()
  .min(1)
  .max(64 * 1024);

export const permissionKeySchema = z.enum(PERMISSION_KEYS as [string, ...string[]]) as z.ZodType<
  (typeof PERMISSION_KEYS)[number]
>;

export const auditEventTypeSchema = z.enum(
  AUDIT_EVENT_TYPE_KEYS as [string, ...string[]],
) as z.ZodType<(typeof AUDIT_EVENT_TYPE_KEYS)[number]>;

export const targetKindSchema = z.enum(TARGET_KINDS);
export const environmentSchema = z.enum(ENVIRONMENTS);
export const executionModeSchema = z.enum(EXECUTION_MODES);
export const outputFormatSchema = z.enum(OUTPUT_FORMATS);
export const httpMethodSchema = z.enum(HTTP_METHODS);
export const statementClassSchema = z.enum(STATEMENT_CLASSES);
export const jobStatusSchema = z.enum(JOB_STATUSES);

// -------------------------------------------------------------- submissions

export const sqlSubmissionSchema = z.object({
  kind: z.literal('sql'),
  targetId: uuidSchema,
  /**
   * Required on a server-scoped target, refused on any other. Which of those
   * applies cannot be decided here -- it depends on the target row -- so the
   * check lives in the submission path, next to the allowlist check it
   * belongs with.
   */
  database: databaseNameSchema.nullable().default(null),
  statement: sqlStatementSchema,
  parameters: z.record(z.string(), z.unknown()).default({}),
  mode: executionModeSchema.default('interactive'),
  outputFormat: outputFormatSchema.default('json'),
  justification: justificationSchema,
});

const headerNameSchema = z.string().regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/, 'Invalid header name');

/**
 * Headers the connector sets itself. Allowing a caller to override Host or
 * Content-Length is a request-smuggling primitive, so they are rejected rather
 * than silently dropped.
 */
export const RESERVED_HEADERS = [
  'host',
  'content-length',
  'transfer-encoding',
  'connection',
  'upgrade',
  'expect',
  'proxy-authorization',
] as const;

export const secretReferenceSchema = z.object({
  bindingId: uuidSchema,
  field: z.string().regex(/^\/[A-Za-z0-9_\-/]*$/, 'Must be a JSON pointer such as /apiKey'),
  injectAs: z.enum(['header', 'query', 'bearer', 'basic-password']),
  name: z.string().max(128).default(''),
});

export const apiSubmissionSchema = z
  .object({
    kind: z.literal('api'),
    targetId: uuidSchema,
    method: httpMethodSchema,
    // Relative to the target base URL. A caller cannot supply a host, which is
    // the first half of the SSRF defence; the connector re-checks the resolved
    // address as the second half.
    path: z
      .string()
      .min(1)
      .max(2048)
      .startsWith('/', 'Path must be relative to the target base URL')
      .refine((p) => !p.includes('..'), 'Path traversal is not permitted'),
    query: z.record(z.string(), z.string()).default({}),
    headers: z.record(headerNameSchema, z.string().max(8192)).default({}),
    body: z
      .string()
      .max(1024 * 1024)
      .nullable()
      .default(null),
    secretRefs: z.array(secretReferenceSchema).max(8).default([]),
    mode: executionModeSchema.default('interactive'),
    justification: justificationSchema,
  })
  .superRefine((value, ctx) => {
    for (const name of Object.keys(value.headers)) {
      if ((RESERVED_HEADERS as readonly string[]).includes(name.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['headers', name],
          message: `${name} is set by the connector and cannot be overridden`,
        });
      }
    }
    for (const ref of value.secretRefs) {
      if ((ref.injectAs === 'header' || ref.injectAs === 'query') && ref.name.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['secretRefs'],
          message: `A ${ref.injectAs} secret reference needs a name`,
        });
      }
    }
  });

export const submissionSchema = z.discriminatedUnion('kind', [
  sqlSubmissionSchema,
  apiSubmissionSchema.innerType(),
]);

export const submitRequestSchema = z.object({
  submission: submissionSchema,
  /**
   * Makes resubmission after a network failure safe. The server returns the
   * original job rather than executing twice.
   */
  idempotencyKey: z.string().min(8).max(128),
  /** Supplied on the second call, after an approval has been granted. */
  approvalRequestId: uuidSchema.optional(),
});

// ---------------------------------------------------------------- approvals

export const approvalDecisionSchema = z.object({
  decision: z.enum(['granted', 'rejected']),
  comment: z.string().trim().min(5, 'Record why you made this decision').max(2000),
});

export const approvalRuleMatchSchema = z
  .object({
    kinds: z
      .array(z.enum(['sql', 'api']))
      .min(1)
      .optional(),
    environments: z.array(environmentSchema).min(1).optional(),
    targetKeys: z.array(targetKeySchema).min(1).optional(),
    statementClasses: z.array(statementClassSchema).min(1).optional(),
    httpMethods: z.array(httpMethodSchema).min(1).optional(),
    procedureNotAllowlisted: z.boolean().optional(),
    rowCountAbove: z.number().int().positive().optional(),
    usesWriteAccount: z.boolean().optional(),
  })
  .refine(
    (m) => Object.values(m).some((v) => v !== undefined),
    'A rule with no conditions would match everything - add at least one condition',
  );

export const approvalRuleSchema = z.object({
  name: z.string().trim().min(3).max(120),
  description: z.string().trim().max(1000).default(''),
  enabled: z.boolean().default(true),
  priority: z.number().int().min(0).max(10_000),
  match: approvalRuleMatchSchema,
  requiredApprovals: z.number().int().min(1).max(5),
  ttlMinutes: z
    .number()
    .int()
    .min(5)
    .max(7 * 24 * 60),
});

// ------------------------------------------------------------------ targets

export const databaseTargetConfigSchema = z.object({
  host: z.string().min(1).max(253),
  port: z.number().int().min(1).max(65535),
  database: z.string().min(1).max(128),
  encrypt: z.boolean().default(true),
  trustServerCertificate: z.boolean().default(false),
});

/** A SQL Server instance. Same fields, minus the database. */
export const serverTargetConfigSchema = z.object({
  host: z.string().min(1).max(253),
  port: z.number().int().min(1).max(65535),
  encrypt: z.boolean().default(true),
  trustServerCertificate: z.boolean().default(false),
});

export const restTargetConfigSchema = z.object({
  baseUrl: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'Only https endpoints may be registered'),
  pathAllowlist: z.array(z.string().startsWith('/')).min(1),
  allowedMethods: z.array(httpMethodSchema).min(1),
  defaultHeaders: z.record(headerNameSchema, z.string().max(8192)).default({}),
  /**
   * Defaults to empty: public addresses only. Listing a private range here
   * is how an internal endpoint is reached, and it is deliberately a target
   * configuration change -- reviewed and audited -- rather than a per-request
   * option. Entries inside link-local or loopback have no effect; those are
   * blocked unconditionally.
   */
  allowedEgressCidrs: z
    .array(
      z
        .string()
        .max(43)
        .regex(/^[0-9a-fA-F.:]+\/\d{1,3}$/, 'Each entry must be a CIDR such as 10.2.0.0/16'),
    )
    .max(20)
    .default([]),
  rateLimitPerSecond: z.number().positive().max(1000).default(10),
  rateLimitBurst: z.number().int().positive().max(5000).default(20),
});

export const createTargetSchema = z
  .object({
    key: targetKeySchema,
    name: z.string().trim().min(3).max(120),
    description: z.string().trim().max(1000).default(''),
    kind: targetKindSchema,
    environment: environmentSchema,
    enabled: z.boolean().default(true),
    /**
     * Ordered, because Zod unions take the first branch that parses and the
     * object schemas are not strict: a config carrying `database` satisfies
     * the server schema too, which would silently strip the field. The
     * database branch going first means a database config keeps its database,
     * and the cross-check below catches the inverse -- a server target
     * arriving with a database it should not have.
     */
    config: z.union([databaseTargetConfigSchema, restTargetConfigSchema, serverTargetConfigSchema]),
    maxConcurrency: z.number().int().min(1).max(32).default(4),
    statementTimeoutMs: z
      .number()
      .int()
      .min(1000)
      .max(4 * 60 * 60 * 1000)
      .default(60_000),
    interactiveRowCap: z.number().int().min(1).max(100_000).default(10_000),
    batchRowCap: z.number().int().min(1).max(50_000_000).default(5_000_000),
    allowScripts: z.boolean().default(false),
    procAllowlist: z.array(z.string().max(256)).default([]),
    schemaAllowlist: z.array(z.string().max(128)).default([]),
    databaseAllowlist: z.array(databaseNameSchema).max(200).default([]),
  })
  /**
   * Kind against config shape.
   *
   * A switch rather than the boolean "does it look REST" test this replaced.
   * That test asked one question -- is there a `baseUrl` -- and answered
   * correctly for two kinds because there were only two kinds. With a third
   * config shape it would have accepted a server target carrying a database
   * name, which is the one combination that must not exist: the database
   * would sit in the config, unused and unenforced, while every submission
   * chose its own.
   */
  .superRefine((value, ctx) => {
    const config: Record<string, unknown> = value.config;
    const reject = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['config'], message });
    };

    switch (value.kind) {
      case 'rest':
        if (!('baseUrl' in config)) reject('A rest target needs a baseUrl configuration');
        break;
      case 'mssql':
      case 'oracle':
        if ('baseUrl' in config) reject('A database target needs host, port and database');
        else if (typeof config['database'] !== 'string' || config['database'] === '') {
          reject('A database target needs host, port and database');
        }
        break;
      case 'mssql_server':
        if ('baseUrl' in config) reject('A server target needs host and port');
        else if ('database' in config) {
          reject(
            'A server target names no database. The database is chosen per query, ' +
              'and listing one here would have no effect.',
          );
        }
        break;
    }

    if (value.databaseAllowlist.length > 0 && !isServerScopedKind(value.kind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['databaseAllowlist'],
        message: 'Only a server-scoped target has a database allowlist',
      });
    }

    /**
     * On a server, a two-part procedure name is sixteen different procedures.
     * Refused here rather than stored and left to never match, because an
     * allowlist entry that silently has no effect is worse than one that is
     * rejected: the administrator believes the procedure is callable without
     * approval, and only finds out otherwise during an incident.
     */
    if (isServerScopedKind(value.kind)) {
      const unqualified = value.procAllowlist.filter((entry) => entry.split('.').length !== 3);
      if (unqualified.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['procAllowlist'],
          message:
            'On a server-scoped target every procedure must be written as ' +
            `database.schema.procedure. Fix: ${unqualified.join(', ')}`,
        });
      }
    }
  });

export const bindSecretSchema = z.object({
  /**
   * Which service account this secret backs. The write account is only ever
   * reachable through a consumed approval token.
   */
  role: z.enum(['read', 'write']),
  secretArn: z
    .string()
    .regex(
      /^arn:aws[a-z-]*:secretsmanager:[a-z0-9-]+:\d{12}:secret:.+$/,
      'Must be a Secrets Manager secret ARN',
    ),
  description: z.string().trim().max(500).default(''),
});

// -------------------------------------------------------------------- roles

export const createRoleSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3)
    .max(80)
    .regex(/^[A-Za-z0-9][A-Za-z0-9 ._-]*$/, 'Use letters, digits, spaces, dots, dashes'),
  description: z.string().trim().max(1000).default(''),
  permissions: z.array(permissionKeySchema).max(PERMISSION_KEYS.length).default([]),
  /**
   * Per-target scoping for the privileged permissions above. A permission with
   * `requiresTargetScope` confers nothing until a row here names the target it
   * applies to, and the server refuses a role that holds one with no scope
   * rather than storing a permission that looks granted and is not.
   */
  targetGrants: z
    .array(z.object({ targetId: uuidSchema, permission: permissionKeySchema }))
    .max(500)
    .default([]),
  /**
   * Supplied only when the role knowingly contains a warn-level toxic
   * combination. Block-level combinations are refused outright.
   */
  sodJustification: z.string().trim().min(20).max(2000).nullable().default(null),
});

export const grantTargetSchema = z.object({
  targetId: uuidSchema,
  permissions: z.array(permissionKeySchema).min(1),
});

export const assignRoleSchema = z.object({
  userId: uuidSchema,
  roleId: uuidSchema,
  /**
   * 'entra-group' is absent deliberately: those assignments are produced only
   * by reconciliation against Microsoft Graph, never by hand, so that the
   * stored row always reflects real group membership.
   */
  source: z.enum(['direct', 'breakglass']).default('direct'),
  /** Time-boxed assignment. Null means permanent until revoked. */
  expiresAt: isoDateSchema.nullable().default(null),
  justification: justificationSchema,
});

export const revokeRoleSchema = z.object({
  userId: uuidSchema,
  roleId: uuidSchema,
  source: z.enum(['direct', 'entra-group', 'breakglass']),
  /**
   * Optional, unlike the justification on assignment. Removing access is the
   * safe direction, and requiring paperwork for it discourages the thing we
   * want people to do promptly when someone changes team.
   */
  reason: z.string().trim().max(1000).optional(),
});

export const mapEntraGroupSchema = z.object({
  groupObjectId: uuidSchema,
  groupDisplayName: z.string().trim().min(1).max(256),
  roleId: uuidSchema,
});

// -------------------------------------------------------------- preferences

/**
 * `null` is a meaningful value, not an absence: it clears the preference and
 * returns the user to following their browser. `.optional()` alone would
 * make "clear it" indistinguishable from "leave it alone", and someone who
 * has moved country and wants the tool to track their laptop again would
 * have no way to say so.
 *
 * The zone is validated against the runtime's own database rather than a
 * pattern, in `isValidTimeZone` -- a pattern accepts `Foo/Bar`, and the
 * first anybody would hear about it is a `RangeError` thrown inside a render.
 */
export const preferencesSchema = z.object({
  timeZone: z
    .string()
    .trim()
    .max(64)
    .refine(isValidTimeZone, { message: 'Not a time zone this system recognises' })
    .nullable(),
});

// -------------------------------------------------------------------- audit

export const auditSearchSchema = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  eventTypes: z.array(auditEventTypeSchema).max(50).optional(),
  actorUpn: z.string().max(320).optional(),
  targetId: uuidSchema.optional(),
  jobId: uuidSchema.optional(),
  /** Full-text across the detail document. */
  q: z.string().max(200).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.number().int().min(1).max(500).default(100),
});

export const evidenceExportSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  /** Narrows the pack to one control's evidence. */
  eventTypes: z.array(auditEventTypeSchema).max(50).optional(),
  includeEntitlementSnapshot: z.boolean().default(true),
  justification: justificationSchema,
});

// ---------------------------------------------------------------- explorer

/**
 * A schema or object name, for the explorer's filter and path parameters.
 *
 * Same reasoning as `databaseNameSchema`: these are bracket-quoted into a
 * generated `SELECT TOP (1000)` or `EXEC`, so the characters that could close
 * the quoting are refused rather than escaped.
 */
export const sqlIdentifierSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z_@#][A-Za-z0-9_$#@ -]*$/, 'Not a valid SQL Server identifier');

export const explorerObjectTypeSchema = z.enum(EXPLORER_OBJECT_TYPES);

/**
 * Why someone is looking.
 *
 * Required on every route that reads a catalogue, and the same
 * `justificationSchema` a statement needs -- browsing a production schema is
 * a read against a production credential, and "which tables does this system
 * have" is the first question of both a support call and a reconnaissance
 * exercise. Entered once when the target is opened and sent with each read,
 * so it is a server-side rule rather than a prompt the client could skip.
 *
 * Not required on the two routes that only compose a statement: they read
 * nothing and disclose nothing the caller did not already supply, and the
 * statement they return is justified when it is submitted.
 */
export const explorerReasonSchema = z.object({
  reason: justificationSchema,
});

export const explorerObjectQuerySchema = explorerReasonSchema.extend({
  type: explorerObjectTypeSchema,
  /**
   * Substring match on the object name, matched case-insensitively.
   *
   * Required once a database holds more objects than the cap: the alternative
   * is an arbitrary first 500 of 4,000 tables, which looks like a complete
   * list and is not. Bounded at 128 and matched with a bound parameter.
   */
  filter: z.string().trim().max(128).default(''),
});

// --------------------------------------------------------------- pagination

export const paginationSchema = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.number().int().min(1).max(200).default(50),
});

export const jobListSchema = paginationSchema.extend({
  status: z.array(jobStatusSchema).optional(),
  targetId: uuidSchema.optional(),
  mine: z.boolean().default(true),
});

// ----------------------------------------------------------- script library

export const sqlDialectSchema = z.enum(SQL_DIALECTS);

/**
 * A tag, normalised at the boundary rather than trusted from the client.
 *
 * Lowercased here so `Billing` and `billing` cannot become two tags. That
 * fragments the vocabulary and makes a filter silently miss things, which is
 * the worst kind of wrong search result because it still looks complete.
 */
export const scriptTagSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(40)
  .regex(
    /^[a-z0-9][a-z0-9 -]*[a-z0-9]$/,
    'Use lowercase letters, digits, spaces and hyphens',
  );

/**
 * Capped in count as well as in shape.
 *
 * A thousand tags on one script is not organisation, it is a denial of
 * service against the tag vocabulary endpoint that every open of the library
 * calls.
 */
export const scriptTagsSchema = z
  .array(scriptTagSchema)
  .max(12)
  .default([])
  .transform((tags) => [...new Set(tags)]);

/**
 * Deduplicated, because `['mssql','mssql']` satisfies the database's
 * containment CHECK perfectly happily and then renders as two badges.
 */
export const scriptDialectsSchema = z
  .array(sqlDialectSchema)
  .min(1, 'Say which engine this runs on')
  .max(SQL_DIALECTS.length)
  .transform((dialects) => [...new Set(dialects)]);

export const scriptNameSchema = z.string().trim().min(2).max(120);

export const createScriptSchema = z.object({
  name: scriptNameSchema,
  description: z.string().trim().max(2_000).default(''),
  /** Reuses the submission cap rather than inventing a second one. */
  statement: sqlStatementSchema,
  dialects: scriptDialectsSchema,
  tags: scriptTagsSchema,
  targetId: uuidSchema.nullable().default(null),
  databaseName: databaseNameSchema.nullable().default(null),
});

/**
 * Metadata only, and never the statement.
 *
 * Changing the text has to append a version, so it has its own route. If it
 * could be done here it would be an in-place edit of something an approver
 * may already have approved, which is the one thing the version history
 * exists to prevent.
 */
export const updateScriptSchema = z.object({
  name: scriptNameSchema.optional(),
  description: z.string().trim().max(2_000).optional(),
  dialects: scriptDialectsSchema.optional(),
  tags: scriptTagsSchema.optional(),
  targetId: uuidSchema.nullable().optional(),
  databaseName: databaseNameSchema.nullable().optional(),
});

export const createScriptVersionSchema = z.object({
  statement: sqlStatementSchema,
  /** Optional, unlike a justification. "Fixed the join" is plenty. */
  note: z.string().trim().max(500).default(''),
});

export const publishScriptSchema = z.object({
  visibility: z.enum(SHAREABLE_VISIBILITIES),
  roleId: uuidSchema.nullable().default(null),
  /** The version to publish. Never "whatever is current" by the time an
   * approver looks at it. */
  version: z.number().int().min(1),
  justification: justificationSchema,
});

export const scriptListSchema = z.object({
  search: z.string().trim().max(120).default(''),
  tags: z.array(scriptTagSchema).max(12).default([]),
  /** Omitted means every scope the caller can see. */
  scope: z.enum(['mine', 'role', 'organisation']).optional(),
  /** The panel sends the current target's dialect by default. */
  dialect: sqlDialectSchema.optional(),
  limit: z.number().int().min(1).max(200).default(100),
});

export const scriptPublicationDecisionSchema = z.object({
  decision: z.enum(['granted', 'rejected']),
  comment: z.string().trim().max(2_000).default(''),
});

// ---------------------------------------------------------------- inference

export type SqlSubmissionInput = z.infer<typeof sqlSubmissionSchema>;
export type ApiSubmissionInput = z.infer<typeof apiSubmissionSchema>;
export type SubmitRequestInput = z.infer<typeof submitRequestSchema>;
export type ApprovalDecisionInput = z.infer<typeof approvalDecisionSchema>;
export type ApprovalRuleInput = z.infer<typeof approvalRuleSchema>;
export type CreateTargetInput = z.infer<typeof createTargetSchema>;
export type BindSecretInput = z.infer<typeof bindSecretSchema>;
export type CreateRoleInput = z.infer<typeof createRoleSchema>;
export type GrantTargetInput = z.infer<typeof grantTargetSchema>;
export type AssignRoleInput = z.infer<typeof assignRoleSchema>;
export type MapEntraGroupInput = z.infer<typeof mapEntraGroupSchema>;
export type AuditSearchInput = z.infer<typeof auditSearchSchema>;
export type EvidenceExportInput = z.infer<typeof evidenceExportSchema>;
export type JobListInput = z.infer<typeof jobListSchema>;
export type ExplorerObjectQueryInput = z.infer<typeof explorerObjectQuerySchema>;
export type CreateScriptInput = z.infer<typeof createScriptSchema>;
export type UpdateScriptInput = z.infer<typeof updateScriptSchema>;
export type CreateScriptVersionInput = z.infer<typeof createScriptVersionSchema>;
export type PublishScriptInput = z.infer<typeof publishScriptSchema>;
export type ScriptListInput = z.infer<typeof scriptListSchema>;
export type ScriptPublicationDecisionInput = z.infer<typeof scriptPublicationDecisionSchema>;
